// dta-data.js - pure config + helpers for the DTA Library (Module 6,
// V6_DESIGN §11.7). No DOM, no network: Vitest covers these directly.

// Mirrors the club's 5 evaluation pillars (Technical / Tactical / Physical /
// Mental / Social), plus warmup and club-info as practical extras.
export const DTA_CATEGORIES = [
  { key: 'warmup',    labelKey: 'dta.catWarmup' },
  { key: 'technical', labelKey: 'dta.catTechnical' },
  { key: 'tactical',  labelKey: 'dta.catTactical' },
  { key: 'physical',  labelKey: 'dta.catPhysical' },
  { key: 'mental',    labelKey: 'dta.catMental' },
  { key: 'social',    labelKey: 'dta.catSocial' },
  { key: 'club',      labelKey: 'dta.catClub' },
];

/**
 * Finer age tags for the library (DTA-only; does NOT touch CONFIG.ageGroups,
 * which drives the 3-band evaluation matrix). One article can carry several,
 * e.g. a FUNdamentals piece tagged U7,U8,U9. Empty age_tags = every age.
 */
export const DTA_AGE_TAGS = ['U7', 'U8', 'U9', 'U10', 'U11', 'U12', 'U14', 'U16', 'U19'];

/** Who a resource is for. 'trainers' content shows only to staff trainers. */
export const DTA_AUDIENCES = ['all', 'trainers'];

/**
 * Pick a resource's title/body in the active language, English fallback when
 * the Dutch variant is blank (the app's standard per-field rule). `base` is
 * 'title' or 'body'; the NL column is `${base}_nl`.
 */
export function dtaText(resource, base, lang = 'en') {
  if (!resource) return '';
  const nl = resource[`${base}_nl`];
  if (lang === 'nl' && nl != null && String(nl).trim()) return nl;
  return resource[base] || '';
}

/**
 * Feed order: pinned items first (the standard warm-up routine stays on
 * top), then newest publish date - so "exercise of the week" naturally
 * rotates down as new ones land.
 */
export function sortResources(resources) {
  return [...(resources || [])].sort((a, b) => {
    if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
    const da = a.published_at || a.created_at || '';
    const db = b.published_at || b.created_at || '';
    return db.localeCompare(da);
  });
}

/**
 * Filter chips: category narrows hard; an age-tag chip keeps untagged
 * resources visible (empty age_tags = relevant to everyone) - tags highlight
 * relevance rather than hide content (§11.7).
 */
export function filterResources(
  resources,
  { category = null, ageTag = null, audience = null, status = null } = {},
) {
  return (resources || []).filter(r => {
    if (category && r.category !== category) return false;
    if (audience && (r.audience || 'all') !== audience) return false;
    // `status` is the director's publish state. Unlike the age chip, this one
    // narrows hard: "show me my drafts" must not include published rows.
    if (status === 'published' && !r.published_at) return false;
    if (status === 'draft' && r.published_at) return false;
    if (ageTag && (r.age_tags || []).length && !(r.age_tags || []).includes(ageTag)) return false;
    return true;
  });
}

/**
 * Free-text search over title + body in BOTH languages (so a Dutch coach
 * finds an article by its Dutch words and vice-versa). Blank query = passthrough.
 */
export function searchResources(resources, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return [...(resources || [])];
  return (resources || []).filter(r => {
    const hay = [r.title, r.title_nl, r.body, r.body_nl]
      .filter(Boolean).join(' ').toLowerCase();
    return hay.includes(q);
  });
}

/**
 * Split resources by audience so the coach feed can show a general library and
 * a separate trainer section. RLS already withholds 'trainers' rows from
 * non-trainers, so `trainers` is naturally empty for a coach.
 */
export function splitByAudience(resources) {
  const all = [], trainers = [];
  (resources || []).forEach(r => ((r.audience === 'trainers') ? trainers : all).push(r));
  return { all, trainers };
}

/**
 * A stable "article of the day": the same resource all day, rotating daily.
 * Seeded by the date string (YYYY-MM-DD) so it is deterministic + testable and
 * never needs Math.random. Returns null when the pool is empty.
 */
export function dailyPick(resources, dateStr) {
  const pool = (resources || []);
  if (!pool.length) return null;
  const seed = String(dateStr || '');
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return pool[h % pool.length];
}

/** Basic sanity for link resources - require an http(s) URL. */
export function isValidLinkUrl(url) {
  return /^https?:\/\/.+\..+/.test((url || '').trim());
}

/**
 * A library title from a PDF filename, for the bulk upload.
 *
 * Underscores become spaces because they are how people avoid spaces in
 * filenames, but HYPHENS are left alone: "SEASON PLAN U7-U8.pdf" means the U7
 * and U8 age band, and turning that into "U7 U8" would quietly rename the
 * club's own documents. Only the final .pdf is stripped, so a title that
 * genuinely contains a dot survives.
 */
export function titleFromFilename(name) {
  const base = String(name || '').split(/[\\/]/).pop();
  return base
    .replace(/\.pdf$/i, '')
    .replace(/_+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);
}
